import React from 'react';
import confetti from 'canvas-confetti';
import { useSpc } from '../../context/SpcContext';
import { ScInfoBadge } from '../ScInfoBadge';
import { isSCSession } from '../../lib/sessionInfo';
import { AchievementCard } from '../Achievements';
import { Reveal, Stagger, Item, CountUp, Logo, motion, EmptyState, Marquee } from '../ui';
import { IMG } from '../../assets';
import { CalendarDays, Award, ShoppingBag, UserPlus, Sparkles, MapPin, Clock, Trophy, Megaphone, Send, X, Activity, ExternalLink, Mic, Utensils, Pencil, Check, Zap, ShieldAlert, ChevronRight, Share2, Users, Flag, Footprints } from 'lucide-react';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function isRecentlyOnline(lastSeen) {
  if (!lastSeen) return false;
  const t = new Date(lastSeen).getTime();
  if (Number.isNaN(t)) return false;
  return (Date.now() - t) < 120000;
}

function OnlineNowCard({ members }) {
  const online = React.useMemo(() => members.filter(m => m.role !== 'Admin' && isRecentlyOnline(m.lastSeen)), [members]);
  return (
    <div className="card span-12" data-testid="online-now-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <h4 style={{ margin: 0 }}>🟢 Online now</h4>
        <span className="pill yellow" data-testid="online-now-count">{online.length} online</span>
      </div>
      {online.length === 0 ? (
        <p className="meta" style={{ marginTop: 10 }}>No members online right now.</p>
      ) : (
        <div className="online-members" data-testid="online-now-strip">
          {online.map(m => (
            <div key={m.id} className="online-member-chip" data-testid={`online-now-member-${m.id}`}>
              <span className="avatar-wrap">
                {m.avatar ? <img src={m.avatar} alt={m.name} /> : <span className="avatar-fallback">{m.name?.[0]}</span>}
                <span className="online-dot" />
              </span>
              <span>{m.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function useCountdown(target) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { days, hours, mins, secs, over: diff === 0 };
}

function nextSessionAt(next) {
  if (!next || !next.dateISO || !next.time) return null;
  try {
    const [h, m] = String(next.time).split(':').map(Number);
    const d = new Date(next.dateISO);
    d.setHours(h || 0, m || 0, 0, 0);
    return d.getTime();
  } catch { return null; }
}

function CountdownHero({ next, go, comingUp = [], myNext = null }) {
  const targetTs = nextSessionAt(next);
  const cd = useCountdown(targetTs);
  const showMyNext = myNext && next && myNext.id !== next.id;
  return (
    <>
      <span className="pill yellow">NEXT UP AT SPC</span>
      {next && cd ? (
        <div className="next-hero-inner">
          <div>
            <div className="eyebrow next-hero-eyebrow" style={{ marginTop: 12 }}>{next.type || 'Session'}{next.mine && <span className="pill soft" style={{ marginLeft: 8 }} data-testid="home-next-mine-badge">✓ You're booked</span>}</div>
            <h2>{next.title}{isSCSession(next) && <ScInfoBadge testId="sc-info-badge-next-hero" className="sc-alert-badge-inline" />}</h2>
            <div className="next-hero-meta">
              <span><CalendarDays size={14} /> {next.date} · {next.time}</span>
              <span><MapPin size={14} /> {next.location}</span>
              {next.coach && <span><Clock size={14} /> {next.coach}</span>}
            </div>
            <button className="btn" style={{ marginTop: 20 }} onClick={() => go('book')}>Open booking</button>
          </div>
          <div className="countdown-grid" data-testid="home-countdown">
            <div className="countdown-cell"><strong>{cd.days}</strong><small>Days</small></div>
            <div className="countdown-cell"><strong>{String(cd.hours).padStart(2, '0')}</strong><small>Hrs</small></div>
            <div className="countdown-cell"><strong>{String(cd.mins).padStart(2, '0')}</strong><small>Min</small></div>
            <div className="countdown-cell"><strong>{String(cd.secs).padStart(2, '0')}</strong><small>Sec</small></div>
          </div>
        </div>
      ) : (
        <div className="next-hero-empty">
          <h3 style={{ marginTop: 14 }}>Nothing booked yet</h3>
          <p>Find your next SPC session, social or event and get it in the diary.</p>
          <button className="btn" style={{ marginTop: 12 }} onClick={() => go('book')}>Find a session</button>
        </div>
      )}
      {showMyNext && (
        <div className="next-hero-mine" data-testid="home-my-next-booking" style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,.08)' }}>
          <span className="pill soft">YOUR NEXT BOOKING</span>
          <div className="next-hero-meta" style={{ marginTop: 10 }}>
            <span><CalendarDays size={14} /> {myNext.title}{isSCSession(myNext) && <ScInfoBadge testId="sc-info-badge-my-next" className="sc-alert-badge-inline" />} — {myNext.date} · {myNext.time}</span>
            <span><MapPin size={14} /> {myNext.location}</span>
          </div>
          <button className="btn secondary" style={{ marginTop: 12 }} onClick={() => go('book')} data-testid="home-view-my-booking">View my booking</button>
        </div>
      )}
      {comingUp.length > 0 && (
        <div className="coming-inline">
          <div className="coming-inline-head">
            <span className="eyebrow" style={{ color: 'var(--spc-yellow)' }}>Coming up next</span>
          </div>
          <div className="coming-strip" data-testid="coming-up-strip">
            {comingUp.map(s => <ComingUpTile key={s.id} s={s} />)}
          </div>
        </div>
      )}
    </>
  );
}

const ACTIVITY_ICONS = {
  kit: <ShoppingBag size={15} />,
  challenge: <Trophy size={15} />,
  member: <UserPlus size={15} />,
  achievement: <Award size={15} />,
  announcement: <Megaphone size={15} />,
  random_act: <Zap size={15} />,
};

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

const ACTIVITY_LINK_LABELS = {
  book: 'Book a session', challenges: 'View challenge', coachhub: 'Open Coaches / Leaders',
  community: 'View in Marketplace', shop: 'Open Shop',
  leaderboard: 'View Leaderboard', benefits: 'View Benefits',
};

function ActivityFeed({ items, role, onRemove, onEdit, go, openModal }) {
  const [editingId, setEditingId] = React.useState(null);
  const [editText, setEditText] = React.useState('');
  if (!items || !items.length) {
    return (
      <EmptyState
        icon={Sparkles}
        title="No activity yet"
        message="Once members start booking, listing kit, and completing challenges, you'll see the club buzz here."
        testId="empty-activity"
      />
    );
  }
  const startEdit = (it) => { setEditingId(it.id); setEditText(it.text); };
  const saveEdit = async () => { const t = editText.trim(); if (t) await onEdit(editingId, t); setEditingId(null); };
  const latestAnnouncementId = items.find(it => it.type === 'announcement')?.id;
  const openItem = (it) => {
    if (it.externalUrl) { window.open(it.externalUrl, '_blank', 'noopener,noreferrer'); return; }
    if (it.link) { go(it.link); return; }
    if (it.type === 'announcement') { openModal('announcementDetail', { text: it.text, at: it.at }); }
  };
  const isClickable = (it) => !!it.link || !!it.externalUrl || it.type === 'announcement';
  return (
    <div className="activity-feed" data-testid="home-activity-feed">
      {items.map((it, i) => (
        <div
          className={`activity-item ${it.type === 'announcement' ? 'activity-announce' : ''} ${it.id === latestAnnouncementId ? 'activity-hero' : ''} ${isClickable(it) && editingId !== it.id ? 'activity-clickable' : ''}`}
          key={it.id || i}
          data-testid={`activity-${it.type}`}
          onClick={() => editingId !== it.id && isClickable(it) && openItem(it)}
        >
          <span className="activity-icon">{ACTIVITY_ICONS[it.type] || <Sparkles size={15} />}</span>
          {editingId === it.id ? (
            <input
              className="activity-edit-input"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              data-testid={`activity-edit-input-${it.id}`}
            />
          ) : (
            <div className="activity-text">
              {it.type === 'announcement' && <strong className="activity-badge">{it.id === latestAnnouncementId ? 'LATEST · ' : 'SPC · '}</strong>}
              {it.text}
              {(it.link || it.externalUrl) && <span className="activity-link-hint">{it.externalUrl ? 'Open link' : (ACTIVITY_LINK_LABELS[it.link] || 'View')} →</span>}
            </div>
          )}
          <span className="activity-time">{it.at ? relativeTime(it.at) : ''}</span>
          {role === 'admin' && it.id && (
            editingId === it.id ? (
              <>
                <button className="activity-remove" title="Save" onClick={(e) => { e.stopPropagation(); saveEdit(); }} data-testid={`activity-save-${it.id}`}><Check size={14} /></button>
                <button className="activity-remove" title="Cancel" onClick={(e) => { e.stopPropagation(); setEditingId(null); }} data-testid={`activity-cancel-${it.id}`}><X size={14} /></button>
              </>
            ) : (
              <>
                <button className="activity-remove" title="Edit" onClick={(e) => { e.stopPropagation(); startEdit(it); }} data-testid={`activity-edit-${it.id}`}><Pencil size={13} /></button>
                <button
                  className="activity-remove"
                  title="Remove"
                  onClick={(e) => { e.stopPropagation(); onRemove(it.id); }}
                  data-testid={`activity-remove-${it.id}`}
                >
                  <X size={14} />
                </button>
              </>
            )
          )}
        </div>
      ))}
    </div>
  );
}

function AdminPostBox({ onPost, onGenerateDraft }) {
  const [text, setText] = React.useState('');
  const [link, setLink] = React.useState('');
  const [externalUrl, setExternalUrl] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [draftOpen, setDraftOpen] = React.useState(false);
  const [draftPrompt, setDraftPrompt] = React.useState('');
  const [draftBusy, setDraftBusy] = React.useState(false);
  const isCustom = link === '__custom__';
  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (t.length < 2) return;
    if (isCustom && !/^https?:\/\//i.test(externalUrl.trim())) return;
    setBusy(true);
    const ok = await onPost(t, isCustom ? '' : link, isCustom ? externalUrl.trim() : '');
    setBusy(false);
    if (ok) { setText(''); setLink(''); setExternalUrl(''); }
  };
  const generateDraft = async () => {
    const p = draftPrompt.trim();
    if (p.length < 2) return;
    setDraftBusy(true);
    const draft = await onGenerateDraft(p);
    setDraftBusy(false);
    if (draft) { setText(draft); setDraftPrompt(''); setDraftOpen(false); }
  };
  return (
    <div>
      <form className="admin-post" onSubmit={submit} data-testid="admin-post-form">
        <span className="admin-post-icon"><Megaphone size={16} /></span>
        <input
          type="text"
          placeholder="Post an update to the club — e.g. Apex window opens Sunday!"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={400}
          data-testid="admin-post-input"
        />
        <button
          type="button"
          className="btn ghost admin-post-ai-btn"
          onClick={() => setDraftOpen(o => !o)}
          data-testid="admin-post-ai-draft-toggle"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Sparkles size={14} /> Draft with AI
        </button>
        <select value={link} onChange={(e) => setLink(e.target.value)} className="admin-post-link-select" data-testid="admin-post-link-select">
          <option value="">No link</option>
          {Object.entries(ACTIVITY_LINK_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          <option value="__custom__">Custom link (URL)…</option>
        </select>
        {isCustom && (
          <input
            type="url"
            placeholder="https://example.com/…"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            className="admin-post-url-input"
            data-testid="admin-post-url-input"
          />
        )}
        <button
          type="submit"
          className="btn"
          disabled={busy || text.trim().length < 2 || (isCustom && !/^https?:\/\//i.test(externalUrl.trim()))}
          data-testid="admin-post-submit"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Send size={14} /> {busy ? 'Posting…' : 'Post'}
        </button>
      </form>
      {draftOpen && (
        <div className="admin-post-ai-panel" data-testid="admin-post-ai-panel">
          <span className="admin-post-icon"><Sparkles size={16} /></span>
          <input
            type="text"
            placeholder="What's the update about? e.g. Sunday 8am hill session moved to the track"
            value={draftPrompt}
            onChange={(e) => setDraftPrompt(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), generateDraft())}
            maxLength={500}
            autoFocus
            data-testid="admin-post-ai-prompt-input"
          />
          <button
            type="button"
            className="btn"
            disabled={draftBusy || draftPrompt.trim().length < 2}
            onClick={generateDraft}
            data-testid="admin-post-ai-generate-btn"
          >
            {draftBusy ? 'Writing…' : 'Generate'}
          </button>
        </div>
      )}
    </div>
  );
}

// Small "Coming up" tile
function ComingUpTile({ s }) {
  const { focusSession } = useSpc();
  const isSocial = (s.type || '').toLowerCase().includes('social');
  return (
    <div
      role="button"
      tabIndex={0}
      className={`coming-tile ${isSocial ? 'coming-tile-social' : ''}`}
      onClick={() => focusSession(s.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') focusSession(s.id); }}
      data-testid={`coming-tile-${s.id}`}
    >
      {isSCSession(s) && <ScInfoBadge testId={`sc-info-badge-${s.id}`} className="sc-alert-badge-corner" />}
      <span className={`pill ${isSocial ? 'green' : 'yellow'} coming-pill`}>{isSocial ? 'Social' : (s.type || 'Session')}</span>
      <h4>{s.title}</h4>
      <div className="coming-meta">
        <span><CalendarDays size={13} /> {s.date} · {s.time}</span>
        <span><MapPin size={13} /> {s.location}</span>
      </div>
      <div className="coming-cta">
        {s.mine ? "You're booked in" : `${s.booked || 0}/${s.capacity || '∞'} booked`}
      </div>
    </div>
  );
}

function RecentAchievements({ items, onOpen }) {
  if (!items || !items.length) return null;
  return (
    <div className="recent-ach-strip" data-testid="home-recent-achievements">
      {items.map((a, i) => (
        <div className="recent-ach-card" key={i} onClick={onOpen} style={{ cursor: 'pointer' }} data-testid={`recent-ach-card-${i}`}>
          <div className="recent-ach-icon"><Award size={17} /></div>
          <strong>{a.title}</strong>
          <small>UNLOCKED · {a.earnedAt}</small>
        </div>
      ))}
    </div>
  );
}

function SpcLiveCard({ ev, postLiveUpdate, canPost }) {
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const isLive = ev.phase === 'live';
  const post = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (t.length < 2) return;
    setBusy(true);
    const ok = await postLiveUpdate(ev.key, t);
    setBusy(false);
    if (ok) setText('');
  };
  return (
    <div className={`spc-live-banner ${isLive ? 'is-live' : 'is-preview'}`} data-testid="spc-live-banner">
      <div className="spc-live-head">
        <span className="spc-live-badge" data-testid="spc-live-badge">{isLive ? <><span className="live-dot" /> LIVE NOW</> : '🏃 THIS WEEKEND'}</span>
        <h2>{ev.race}</h2>
        <p>{ev.sport}{ev.location ? ` · ${ev.location}` : ''}</p>
      </div>
      <div className="spc-live-cols">
        <div className="spc-live-col">
          <span className="eyebrow">🏁 Racing ({ev.racing.length})</span>
          {ev.racing.length ? (
            <div className="spc-live-avatars" data-testid="spc-live-racing-list">{ev.racing.map((m, i) => <span key={i} className="spc-live-chip" data-testid={`live-racer-${i}`} title={m.note || ''}>{m.name}</span>)}</div>
          ) : <p className="meta">No one's marked as racing yet.</p>}
        </div>
        <div className="spc-live-col">
          <span className="eyebrow">📣 Supporting ({ev.supporting.length})</span>
          {ev.supporting.length ? (
            <div className="spc-live-avatars" data-testid="spc-live-supporting-list">{ev.supporting.map((m, i) => <span key={i} className="spc-live-chip soft" data-testid={`live-supporter-${i}`} title={m.note || ''}>{m.name}</span>)}</div>
          ) : <p className="meta">No one's marked as supporting yet.</p>}
        </div>
      </div>
      {isLive && canPost && (
        <form className="spc-live-post" onSubmit={post} data-testid={`spc-live-post-form-${ev.key}`}>
          <input type="text" placeholder="Post a live update…" value={text} onChange={(e) => setText(e.target.value)} maxLength={400} data-testid="spc-live-post-input" />
          <button className="btn" disabled={busy || text.trim().length < 2} data-testid="spc-live-post-submit">{busy ? 'Posting…' : 'Post'}</button>
        </form>
      )}
      {ev.updates.length > 0 && (
        <div className="spc-live-updates" data-testid="spc-live-updates">
          {ev.updates.map(u => (
            <div className="spc-live-update-item" key={u.id}>
              <span className="spc-live-update-time">{relativeTime(u.createdAt)}</span>
              <span>{u.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeaturedEventBanner({ sessions, focusSession }) {
  const { toast, sessionPhotoUrl } = useSpc();
  const featured = React.useMemo(() => {
    const todayISO = new Date().toISOString().slice(0, 10);
    return (sessions || [])
      .filter(s => s.featured && s.dateISO >= todayISO && (s.booked || 0) < (s.capacity || Infinity))
      .sort((a, b) => a.dateISO.localeCompare(b.dateISO))[0];
  }, [sessions]);
  if (!featured) return null;
  const spotsLeft = Number.isFinite(featured.capacity) ? Math.max(0, featured.capacity - (featured.booked || 0)) : null;
  const ctaLabel = featured.mine ? 'View my booking' : (featured.priceAmount ? `Get tickets · ${featured.price}` : 'Book now — it\'s free');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(featured.dateISO); target.setHours(0, 0, 0, 0);
  const daysLeft = Math.round((target - today) / 86400000);
  const countdownLabel = daysLeft <= 0 ? 'Today!' : daysLeft === 1 ? '1 day left' : `${daysLeft} days left`;
  const isUrgent = daysLeft <= 3;
  const handleClick = (e) => {
    const x = e.clientX ? e.clientX / window.innerWidth : 0.5;
    const y = e.clientY ? e.clientY / window.innerHeight : 0.5;
    confetti({ particleCount: 70, spread: 65, startVelocity: 38, origin: { x, y }, colors: ['#e1e20a', '#fff6b0', '#ffffff'], zIndex: 300, scalar: 0.9 });
    focusSession(featured.id);
  };
  const handleShare = async (e) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/?nav=book`;
    const shareText = `Join me at ${featured.title} — ${featured.date} at ${featured.time}! `;
    if (navigator.share) {
      try { await navigator.share({ title: featured.title, text: shareText, url: shareUrl }); } catch { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(`${shareText}${shareUrl}`); toast('Link copied — share it with a friend!'); }
      catch { toast('Could not copy link — try again'); }
    }
  };
  return (
    <Reveal>
      <div
        role="button"
        tabIndex={0}
        className="featured-event-cta"
        data-testid="featured-event-banner"
        onClick={handleClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); }}
        style={featured.hasPhoto ? { backgroundImage: `linear-gradient(120deg, rgba(10,10,11,.62), rgba(10,10,11,.9)), url(${sessionPhotoUrl(featured.id, featured.photoV)})` } : undefined}
      >
        <span className="featured-event-shine" aria-hidden="true" />
        <div className="featured-event-top">
          <span className="featured-event-badge">Featured Event</span>
          <span className={`featured-event-countdown ${isUrgent ? 'is-urgent' : ''}`} data-testid="featured-event-countdown">
            <Clock size={12} /> {countdownLabel}
          </span>
        </div>
        <div className="featured-event-body">
          <h3>{featured.title}</h3>
          <p>{featured.date} · {featured.time}{spotsLeft !== null && spotsLeft <= 15 ? ` · Only ${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} left!` : ''}</p>
        </div>
        <div className="featured-event-actions">
          <span className="featured-event-btn">{ctaLabel} <ChevronRight size={16} /></span>
          <button type="button" className="featured-event-share-btn" data-testid="featured-event-share-btn" onClick={handleShare} aria-label="Share this event with a friend">
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>
    </Reveal>
  );
}

function AskCoachBanner({ openModal }) {
  return (
    <Reveal>
      <button type="button" className="ask-coach-cta" data-testid="home-ask-coach-banner" onClick={() => openModal('aiCoach')}>
        <span className="ask-coach-cta-icon"><Sparkles size={18} /></span>
        <span className="ask-coach-cta-text">
          <strong>Ask Smartie</strong>
          <small>Instant answers &amp; personalized tips, just for you</small>
        </span>
        <ChevronRight size={18} className="ask-coach-cta-chevron" />
      </button>
    </Reveal>
  );
}

function CoachHubBanner({ go, alert }) {
  const hasAlert = !!(alert && (alert.hasUnvotedPoll || alert.hasCheckinTask));
  return (
    <Reveal>
      <button type="button" className="coach-hub-cta" data-testid="coach-hub-banner" onClick={() => go('coachhub')}>
        <span className="coach-hub-cta-icon">
          <ShieldAlert size={18} />
          {hasAlert && <span className="coach-hub-alert-dot" data-testid="coach-hub-alert-dot" />}
        </span>
        <span className="coach-hub-cta-text">
          <strong>Coaches / Leaders Hub</strong>
          <small>Weekly poll, safety cards &amp; private resources</small>
        </span>
        <ChevronRight size={18} className="coach-hub-cta-chevron" />
      </button>
    </Reveal>
  );
}

function RaceHubCTA({ openRaceHub }) {
  return (
    <Reveal>
      <button type="button" className="coach-hub-cta race-hub-cta" data-testid="home-add-race-banner" onClick={() => openRaceHub('form')}>
        <span className="coach-hub-cta-icon race-hub-cta-icon"><Flag size={18} /></span>
        <span className="coach-hub-cta-text">
          <strong>Racing soon?</strong>
          <small>Add your race to Race Hub so the club can cheer you on</small>
        </span>
        <span className="race-hub-cta-label">Add my race</span>
        <ChevronRight size={18} className="coach-hub-cta-chevron" />
      </button>
    </Reveal>
  );
}

function ParkrunCTA({ go, parkrun }) {
  const pr = parkrun || { myLocation: null, groups: [] };
  const totalGoing = pr.groups.reduce((n, g) => n + g.attendees.length, 0);
  return (
    <Reveal>
      <button type="button" className="coach-hub-cta parkrun-cta" data-testid="home-parkrun-banner" onClick={() => go('parkrun')}>
        <span className="coach-hub-cta-icon parkrun-cta-icon"><Footprints size={18} /></span>
        <span className="coach-hub-cta-text">
          <strong>{pr.myLocation ? `You're down for ${pr.myLocation}` : 'Saturday Parkrun'}</strong>
          <small>{totalGoing > 0 ? `${totalGoing} SPC member${totalGoing === 1 ? '' : 's'} going this week` : 'Say which parkrun you\'re doing this week'}</small>
        </span>
        <span className="parkrun-cta-label">{pr.myLocation ? 'View' : "I'm in"}</span>
        <ChevronRight size={18} className="coach-hub-cta-chevron" />
      </button>
    </Reveal>
  );
}

function FeedTeaserBanner({ go, unread }) {
  return (
    <Reveal>
      <button type="button" className="coach-hub-cta feed-teaser-cta" data-testid="feed-teaser-banner" onClick={() => go('feed')}>
        <span className="coach-hub-cta-icon">
          <Users size={18} />
          {unread > 0 && <span className="coach-hub-alert-dot" data-testid="feed-teaser-dot" />}
        </span>
        <span className="coach-hub-cta-text">
          <strong>The Clubhouse Wall{unread > 0 ? ` · ${unread} new` : ''}</strong>
          <small>Posts, photos &amp; videos the whole club can like and comment on</small>
        </span>
        <ChevronRight size={18} className="coach-hub-cta-chevron" />
      </button>
    </Reveal>
  );
}

function SpcLiveBanner({ liveEvents, postLiveUpdate, canPost }) {
  const [open, setOpen] = React.useState(false);
  if (!liveEvents || !liveEvents.length) return null;
  const liveCount = liveEvents.filter(e => e.phase === 'live').length;
  const previewCount = liveEvents.filter(e => e.phase === 'preview').length;
  const hasLive = liveCount > 0;
  const hasPreview = previewCount > 0;
  const headline = hasLive && hasPreview
    ? `${liveCount} LIVE NOW · ${previewCount} this weekend`
    : hasLive ? 'LIVE NOW' : "Who's racing this weekend?";
  const raceNames = liveEvents.map(e => e.race).join(' · ');
  const totalRacing = liveEvents.reduce((n, e) => n + e.racing.length, 0);
  return (
    <Reveal>
      <button
        type="button"
        className={`spc-live-cta ${hasLive ? 'is-live' : 'is-preview'}`}
        data-testid="spc-live-cta"
        onClick={() => setOpen(o => !o)}
      >
        <span className="spc-live-cta-badge">{hasLive ? <><span className="live-dot" /> LIVE</> : '🏃'}</span>
        <span className="spc-live-cta-text">
          <strong>{headline}</strong>
          <small>{raceNames} · {totalRacing} SPC {totalRacing === 1 ? 'racer' : 'racers'}</small>
        </span>
        <span className={`spc-live-cta-chevron ${open ? 'open' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="spc-live-wrap" data-testid="spc-live-wrap">
          {liveEvents.map(ev => <SpcLiveCard key={ev.key} ev={ev} postLiveUpdate={postLiveUpdate} canPost={canPost} />)}
        </div>
      )}
    </Reveal>
  );
}

function StravaClub() {
  return (
    <div className="card span-4 strava-card" data-testid="strava-club-card">
      <p className="cta-subtitle" style={{ marginTop: 0 }}>If it's not on Strava, it never happened.</p>
      <div className="strava-embed-frame" data-testid="strava-embed-frame">
        <iframe
          frameBorder="0"
          height="100%"
          scrolling="no"
          title="SPC Strava club activity"
          src="https://www.strava.com/clubs/2251927/latest-rides/4e021b8734382238c7d73af09b8a93756ead3bc5?show_rides=false"
          width="100%"
        />
      </div>
      <a href="https://www.strava.com/clubs/2251927" target="_blank" rel="noopener noreferrer" className="btn secondary" style={{ marginTop: 'auto' }} data-testid="strava-view-club-link">
        View full club activity
      </a>
    </div>
  );
}

export default function Home({ go }) {
  const { state, role, user, openRaceHub, openModal, installApp, canInstall, postAnnouncement, generateAnnouncementDraft, removeActivity, editActivity, openSPC, getOnlineCount, postLiveUpdate, focusSession } = useSpc();
  const canPostLiveUpdate = role === 'admin' || (user?.permissions || []).includes('create_sessions');
  const isLeader = role === 'admin' || (user?.permissions || []).includes('view_polls') || !!user?.canScan;
  const [dismissed, setDismissed] = React.useState(() => localStorage.getItem('spc_hide_install_banner') === '1');
  const isIOS = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone = typeof window !== 'undefined' && (window.navigator.standalone || window.matchMedia?.('(display-mode: standalone)').matches);
  const showIOSInstall = isIOS && !isStandalone;

  // Upcoming sessions (today onwards), sorted by dateISO+time via backend.
  // Once a session's actual start date+time has passed, it drops off "Next up" immediately —
  // comparing only the calendar date kept an already-started/finished session showing all day.
  const upcoming = React.useMemo(() => {
    const now = Date.now();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return (state.sessions || []).filter(s => {
      if (!s.dateISO) return true;
      const startTs = nextSessionAt(s);
      if (startTs != null) return startTs > now;
      return new Date(s.dateISO).getTime() >= today.getTime();
    });
  }, [state.sessions]);

  const mineUpcoming = upcoming.filter(s => s.mine);
  const next = upcoming[0];
  const myNext = mineUpcoming[0];

  // Coming-up strip: next 3 excluding the one already shown as NEXT UP;
  // ensure at least one is a Social if any social exists in upcoming.
  const comingUp = React.useMemo(() => {
    if (!next) return [];
    const pool = upcoming.filter(s => s.id !== next.id);
    const picks = pool.slice(0, 3);
    const hasSocial = picks.some(s => (s.type || '').toLowerCase().includes('social'));
    if (!hasSocial) {
      const nextSocial = pool.find(s => (s.type || '').toLowerCase().includes('social') && !picks.some(p => p.id === s.id));
      if (nextSocial) {
        if (picks.length < 3) picks.push(nextSocial);
        else picks[picks.length - 1] = nextSocial;
      }
    }
    return picks;
  }, [next, upcoming]);
  const hasChallenge = !!state.challenge?.id;
  const pct = hasChallenge ? Math.min(100, Math.round((state.challenge.progress / state.challenge.target) * 100)) : 0;
  const challengeCountdown = useCountdown(hasChallenge && state.challenge.endsAt ? new Date(state.challenge.endsAt).getTime() : null);

  // Marquee ticker items — mix real data with a couple of evergreen hits
  const marqueeItems = React.useMemo(() => {
    const items = [];
    (state.recentActivity || []).slice(0, 6).forEach(a => items.push(a.text));
    if (state.member?.name) items.push(`Welcome back, ${state.member.name.split(' ')[0]} · Let's move`);
    if (state.sessions?.length) items.push(`${state.sessions.length} session${state.sessions.length === 1 ? '' : 's'} on the schedule`);
    if ((state.apexInterest || {}).count > 0) items.push(`${state.apexInterest.count} member${state.apexInterest.count === 1 ? '' : 's'} waiting for Apex kit`);
    if (items.length === 0) items.push('Welcome to the SPC Clubhouse — let\'s move.');
    return items;
  }, [state.recentActivity, state.sessions, state.apexInterest, state.member]);

  // Recent achievements: sorted newest first, take top 6
  const catalogueMap = React.useMemo(() => {
    const map = {};
    (state.achievementCatalogue || []).forEach(a => { map[a.id] = a; });
    return map;
  }, [state.achievementCatalogue]);
  const recentAchievements = React.useMemo(() => {
    const arr = state.unlockedAchievements || [];
    return arr.slice(-6).reverse().map(u => ({
      ...u,
      title: (catalogueMap[u.id] || {}).title || 'Achievement',
    }));
  }, [state.unlockedAchievements, catalogueMap]);

  // Online-now count for the member badge — admin gets the full list from state.members instead.
  const [onlineCount, setOnlineCount] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    const load = () => getOnlineCount().then(n => { if (alive) setOnlineCount(n); });
    load();
    const id = setInterval(load, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [getOnlineCount]);

  if (role === 'admin') {
    const activeMembers = state.members.filter(m => m.role !== 'Admin' && String(m.status || '').toLowerCase() === 'active').length;
    const bookings = state.sessions.reduce((n, s) => n + s.booked, 0);
    const pending = state.members.filter(m => m.role !== 'Admin' && String(m.approvalStatus || '').toLowerCase() === 'pending').length;
    // Admin sees the next upcoming session across the whole club
    const adminNext = next;
    return (
      <>
        <FeaturedEventBanner sessions={state.sessions} focusSession={focusSession} />
        <Marquee items={marqueeItems} />
        <SpcLiveBanner liveEvents={state.liveEvents} postLiveUpdate={postLiveUpdate} canPost={canPostLiveUpdate} />
        {/* Club Feed parked — do not release yet. Re-add <FeedTeaserBanner /> here when ready. */}
        {isLeader && <CoachHubBanner go={go} alert={state.coachHubAlert} />}
        <Reveal>
          <div className="hero">
            <div className="hero-bg" style={{ backgroundImage: `url(${IMG.speed})` }} />
            <div className="hero-veil" />
            <Logo size={340} glow={false} className="hero-shield" />
            <div className="hero-inner">
              <div className="eyebrow">SPC Admin Centre</div>
              <h2>Run the club from <span className="hl">one place.</span></h2>
              <p>Train. Book. Race. Connect. Earn. Everything SPC, all in one place.</p>
              <div className="hero-actions"><button className="btn" onClick={() => go('profile')}>Open Admin Centre</button><button className="btn secondary" onClick={() => go('book')}>Manage bookings</button></div>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="card span-12 next-hero" data-testid="next-up-hero-admin" style={{ marginTop: 18, marginBottom: 20 }}>
            <CountdownHero next={adminNext} go={go} comingUp={comingUp} myNext={myNext} />
          </div>
        </Reveal>

        <RaceHubCTA openRaceHub={openRaceHub} />
        <ParkrunCTA go={go} parkrun={state.parkrun} />

        <Stagger className="grid">
          <Item className="card span-4"><div className="stat"><div><span>Active members</span><br /><strong><CountUp value={activeMembers} /></strong></div><span className="pill yellow">Active</span></div></Item>
          <Item className="card span-4"><div className="stat"><div><span>Upcoming bookings</span><br /><strong><CountUp value={bookings} /></strong></div><span className="pill">Across events</span></div></Item>
          <Item className="card span-4"><div className="stat"><div><span>Awaiting approval</span><br /><strong><CountUp value={pending} /></strong></div><span className="pill yellow">Review</span></div></Item>
        </Stagger>

        <Reveal>
          <div style={{ marginTop: 16 }}><OnlineNowCard members={state.members || []} /></div>
        </Reveal>

        <div className="grid" style={{ marginTop: 8 }}>
          <StravaClub />
          <div className="card span-4" data-testid="home-cta-podcast">
            <p className="cta-subtitle" style={{ marginTop: 0 }}>Training talk, race stories and coaching tips from the SPC team.</p>
            <div className="cta-card-img"><img src="https://customer-assets-jt897jd0.emergentagent.net/job_spc-booking-app/artifacts/36a6j2yr_WhatsApp%20Image%202026-02-14%20at%2016.03.29.jpeg" alt="SPC Podcast" /></div>
          <button className="btn secondary" style={{ marginTop: 'auto' }} onClick={() => openSPC('podcast')} data-testid="home-cta-podcast-btn">Listen now</button>
          </div>
          <div className="card span-4" data-testid="home-cta-nutrition">
            <p className="cta-subtitle" style={{ marginTop: 0 }}>Fuelling plans and nutrition coaching to back up your training.</p>
            <div className="cta-card-img"><img src="https://customer-assets-jt897jd0.emergentagent.net/job_spc-booking-app/artifacts/ebbskecm_Screenshot%202026-09-02%20144439.png" alt="SPC Performance Nutrition" /></div>
          <button className="btn secondary" style={{ marginTop: 'auto' }} onClick={() => openSPC('nutrition')} data-testid="home-cta-nutrition-btn">Explore nutrition</button>
          </div>
        </div>
      </>
    );
  }

  const firstName = (state.member.name || 'Member').split(' ')[0];
  return (
    <>
      <FeaturedEventBanner sessions={state.sessions} focusSession={focusSession} />
      <Reveal>
        <div className="hero">
          <div className="hero-bg hero-bg-club" style={{ backgroundImage: `url(${IMG.heroRun})` }} />
          <div className="hero-veil" />
          <Logo size={340} glow={false} className="hero-shield" />
          <div className="hero-inner">
            <div className="eyebrow">Your SPC</div>
            <h2>{greeting()},<br /><span className="hl">{firstName}.</span> Let's move.</h2>
            <p>Train. Book. Race. Connect. Earn. Everything SPC, all in one place.</p>
            <motion.div className="hero-chips" initial="hidden" animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } } }}>
              {[[state.member.points, 'SPC Points'], [hasChallenge ? `${state.challenge.progress}/${state.challenge.target}` : '—', 'Challenge'], [mineUpcoming.length, mineUpcoming.length === 1 ? 'Booking' : 'Bookings']].map(([b, s], i) => (
                <motion.div className="chip" key={i} variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}>
                  <b>{b}</b><span>{s}</span>
                </motion.div>
              ))}
            </motion.div>
            {onlineCount !== null && (
              <div className="pill soft" style={{ marginTop: 12, display: 'inline-flex' }} data-testid="member-online-badge">🟢 {onlineCount} member{onlineCount === 1 ? '' : 's'} online now</div>
            )}
            <div className="hero-actions">
              <button className="btn" onClick={() => go('book')}>Book a session</button>
              <button className="btn secondary" onClick={() => openRaceHub('view')}>Who's Racing?</button>
            </div>
          </div>
        </div>
      </Reveal>

      <Marquee items={marqueeItems} />
      <SpcLiveBanner liveEvents={state.liveEvents} postLiveUpdate={postLiveUpdate} canPost={canPostLiveUpdate} />
      <RaceHubCTA openRaceHub={openRaceHub} />
      <ParkrunCTA go={go} parkrun={state.parkrun} />
      {/* Club Feed parked — do not release yet. Re-add <FeedTeaserBanner /> here when ready. */}
      {isLeader && <CoachHubBanner go={go} alert={state.coachHubAlert} />}

      {!dismissed && (canInstall || showIOSInstall) && (
        <Reveal delay={0.05}>
          <div className="install-banner install-banner--live" data-testid="install-auto-banner">
            <div><strong>{showIOSInstall ? 'Turn on push notifications' : 'One-tap install'}</strong><small>{showIOSInstall ? "iPhone only shows session reminders once SPC is added to your Home Screen — takes 10 seconds." : 'Add SPC to your Home Screen — feels like a proper app, works offline.'}</small></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" onClick={showIOSInstall ? () => openModal('iosInstall') : installApp} data-testid="install-app-btn-auto">{showIOSInstall ? 'Show me how' : 'Install now'}</button>
              <button className="btn ghost" onClick={() => { localStorage.setItem('spc_hide_install_banner', '1'); setDismissed(true); }} data-testid="install-dismiss-btn">Not now</button>
            </div>
          </div>
        </Reveal>
      )}

      {recentAchievements.length > 0 && (
        <>
          <div className="section-head"><div><div className="eyebrow">Recently earned</div><h2>Your latest badges</h2></div><button className="btn ghost" onClick={() => openModal('achievements')} data-testid="home-open-cabinet">View all</button></div>
          <RecentAchievements items={recentAchievements} onOpen={() => openModal('achievements')} />
        </>
      )}

      <div className="section-head"><div><div className="eyebrow">Today</div><h2>Your SPC at a glance</h2><p>Everything that matters, right now.</p></div></div>
      <Reveal>
        <div className="card span-12 next-hero" data-testid="next-up-hero" style={{ marginBottom: 20 }}>
          <CountdownHero next={next} go={go} comingUp={comingUp} myNext={myNext} />
        </div>
      </Reveal>

      <Stagger className="grid">
        <Item className="card span-6 accent">
          <span className="pill yellow">WHO'S RACING?</span>
          <h3 style={{ marginTop: 14 }}>Who's racing this weekend?</h3>
          <p>See who's racing, supporting, pacing or spectating — or add your own event.</p>
          <div className="hero-actions" style={{ marginTop: 16 }}><button className="btn" onClick={() => openRaceHub('view')}>Open Race Hub</button><button className="btn ghost" onClick={() => openRaceHub('form')}>Add my race</button></div>
        </Item>

        <Item className="card span-6 points-card" data-testid="home-challenge-cta">
          {hasChallenge ? (
            <>
              <span className="pill yellow">🎯 CHALLENGE LIVE</span>
              <h3 style={{ marginTop: 12, marginBottom: 2 }}>{state.challenge.title}</h3>
              <div className="points-balance"><CountUp value={pct} duration={1300} /><span>%</span></div>
              <div className="points-sub">
                {state.challenge.progress} of {state.challenge.target} {state.challenge.unit}
                {challengeCountdown && !challengeCountdown.over && ` · ${challengeCountdown.days}d left`}
              </div>
              <button className="btn" style={{ marginTop: 18 }} onClick={() => go('challenges')} data-testid="home-open-challenges">Log your progress →</button>
            </>
          ) : (
            <>
              <span className="pill yellow">MONTHLY CHALLENGE</span>
              <h3 style={{ marginTop: 14 }}>No challenge running right now</h3>
              <p>Check back soon — the next SPC Challenge will show up here the moment it goes live.</p>
              <button className="btn ghost" style={{ marginTop: 18 }} onClick={() => go('challenges')} data-testid="home-open-challenges">View Challenges</button>
            </>
          )}
        </Item>
        <Item className="card span-6 points-card">
          <span className="pill yellow">SPC POINTS</span>
          <div className="points-balance"><CountUp value={state.member.points} /><span>pts</span></div>
          <div className="points-sub">available to spend · {state.member.lifetimePoints} lifetime</div>
          <button className="btn" style={{ marginTop: 18 }} onClick={() => go('benefits')}>View rewards</button>
        </Item>

        <Item className="card span-6">
          <span className="pill yellow">YOUR STORY</span>
          <h3 style={{ marginTop: 14 }}><CountUp value={state.member.sessionsAttended || 0} /> sessions · {state.unlockedAchievements.length} achievements</h3>
          <p>Your attendance, challenges, points, referrals and milestones all build your member story.</p>
          <div className="hero-actions" style={{ marginTop: 16 }}><button className="btn" onClick={() => go('profile')}>View My SPC</button><button className="btn ghost" onClick={() => openModal('achievements')}>Achievement Cabinet</button></div>
        </Item>
      </Stagger>

      <div className="section-head"><div><div className="eyebrow">Upgrade</div><h2>Level up your training</h2><p>Coaching, plans and premium extras to go further with SPC.</p></div></div>
      <Stagger className="grid">
        <Item className="card span-4 accent" data-testid="home-cta-coaching">
          <p style={{ marginTop: 0 }}>Personalised plans, S&amp;C and nutrition support from the SPC coaching team.</p>
          <div className="cta-card-img"><img src="https://customer-assets-jt897jd0.emergentagent.net/job_spc-booking-app/artifacts/1l08u76r_WhatsApp%20Image%202026-08-09%20at%2020.13.19%20%283%29.jpeg" alt="Coaching" /></div>
          <button className="btn" style={{ marginTop: 16 }} onClick={() => openSPC('zone1')} data-testid="home-cta-coaching-btn">Explore coaching</button>
        </Item>
        <Item className="card span-4 accent" data-testid="home-cta-plans">
          <p style={{ marginTop: 0 }}>Structured training plans from 5k to ultra — pick yours and get running.</p>
          <div className="cta-card-img"><img src="https://customer-assets-jt897jd0.emergentagent.net/job_spc-booking-app/artifacts/bl90b1ti_WhatsApp%20Image%202026-07-10%20at%2005.53.40.jpeg" alt="Training Plans" /></div>
          <button className="btn" style={{ marginTop: 16 }} onClick={() => openSPC('plans')} data-testid="home-cta-plans-btn">Browse plans</button>
        </Item>
        <Item className="card span-4 accent" data-testid="home-cta-planplus">
          <p style={{ marginTop: 0 }}>Premium plans, extra sessions and exclusive member perks for £14.99/month.</p>
          <div className="cta-card-img"><img src="https://customer-assets-jt897jd0.emergentagent.net/job_spc-booking-app/artifacts/8n0sikna_f3b15b8c-7d65-4d23-83d3-7df9d3926032.webp" alt="SPC Plan+" /></div>
          <button className="btn" style={{ marginTop: 16 }} onClick={() => openSPC('planplus')} data-testid="home-cta-planplus-btn">Discover Plan+</button>
        </Item>
      </Stagger>

      <div className="grid" style={{ marginTop: 8 }}>
        <StravaClub />
        <div className="card span-4" data-testid="home-cta-podcast">
          <p className="cta-subtitle" style={{ marginTop: 0 }}>Training talk, race stories and coaching tips from the SPC team.</p>
          <div className="cta-card-img"><img src="https://customer-assets-jt897jd0.emergentagent.net/job_spc-booking-app/artifacts/36a6j2yr_WhatsApp%20Image%202026-02-14%20at%2016.03.29.jpeg" alt="SPC Podcast" /></div>
          <button className="btn secondary" style={{ marginTop: 12 }} onClick={() => openSPC('podcast')} data-testid="home-cta-podcast-btn">Listen now</button>
        </div>
        <div className="card span-4" data-testid="home-cta-nutrition">
          <p className="cta-subtitle" style={{ marginTop: 0 }}>Fuelling plans and nutrition coaching to back up your training.</p>
          <div className="cta-card-img"><img src="https://customer-assets-jt897jd0.emergentagent.net/job_spc-booking-app/artifacts/ebbskecm_Screenshot%202026-09-02%20144439.png" alt="SPC Performance Nutrition" /></div>
          <button className="btn secondary" style={{ marginTop: 12 }} onClick={() => openSPC('nutrition')} data-testid="home-cta-nutrition-btn">Explore nutrition</button>
        </div>
      </div>
    </>
  );
}
